import { useState } from 'react';
import { useApi, dollars, today, fmtDate, navigate, type Row } from '../lib';
import { Panel, Table, Loading, ErrorBox, Stat, Tabs } from '../ui';

export function Reports() {
  const y = today().slice(0, 4);
  const [from, setFrom] = useState(`${y}-01-01`);
  const [to, setTo] = useState(today());
  const [tab, setTab] = useState('shop');
  const { data, error, reload } = useApi<Row>(tab === 'shop' ? `/api/reports?from=${from}&to=${to}` : null, [from, to, tab]);
  const presets: [string, string, string][] = [
    ['This month', today().slice(0, 8) + '01', today()],
    ['This year', `${y}-01-01`, today()],
    ['Last year', `${Number(y) - 1}-01-01`, `${Number(y) - 1}-12-31`],
  ];
  return (
    <>
      <div className="page-head"><div><h1>Reports</h1><p>How the shop is doing. Based on invoices issued in the period.</p></div></div>
      <div className="toolbar">
        {presets.map(([l, a, b]) => <button key={l} className={'chip' + (from === a && to === b ? ' on' : '')} onClick={() => { setFrom(a); setTo(b); }}>{l}</button>)}
        <label className="inline">From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label className="inline">To <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
      </div>
      <Tabs tabs={[{ id: 'shop', label: 'Shop' }, { id: 'discounts', label: 'Discounts' }, { id: 'runs', label: 'Parts runs' }]} value={tab} onChange={setTab} />
      {tab === 'discounts' ? <DiscountReport from={from} to={to} /> : tab === 'runs' ? <RunsReport from={from} to={to} /> : <>
        <ErrorBox error={error} onRetry={reload} />
        {!data ? <Loading /> : <ReportBody d={data} />}</>}
    </>
  );
}

function ReportBody({ d }: { d: Row }) {
  const t = d.totals;
  const partsMargin = t.parts - t.partsCost;
  const gross = t.labor + t.supplies + partsMargin - d.payroll;
  const max = Math.max(1, ...(d.months as Row[]).map(m => m.total));
  return (
    <>
      <div className="stats">
        <Stat label="Revenue (invoiced)" value={dollars(t.revenue)} hint={`${t.invoices} invoices · incl. ${dollars(t.tax)} tax`} />
        <Stat label="Labor" value={dollars(t.labor)} />
        <Stat label="Parts profit" value={dollars(partsMargin)} hint={`${dollars(t.parts)} sold − ${dollars(t.partsCost)} cost${t.parts ? ` · ${Math.round(partsMargin / t.parts * 100)}% margin` : ''}`} />
        <Stat label="Payroll paid" value={dollars(d.payroll)} />
        <Stat label="Shop gross profit" value={dollars(gross)} hint="Labor + supplies + parts profit − payroll. Before rent, utilities, taxes." tone={gross < 0 ? 'warn' : ''} />
        <Stat label="Parts lost (counts)" value={dollars(d.shrink?.lost || 0)} tone={d.shrink?.lost ? 'warn' : ''} hint={d.shrink?.found ? `${dollars(d.shrink.found)} found extra` : undefined} />
      </div>
      <div className="grid-2">
        <Panel title="Revenue by month">
          {(d.months as Row[]).length ? (
            <div className="bars" role="img" aria-label="Revenue by month">
              {(d.months as Row[]).map(m => (
                <div className="bar-row" key={m.month}>
                  <span>{new Date(m.month + '-15').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}</span>
                  <div className="bar"><i style={{ width: `${(m.labor / max) * 100}%` }} className="labor" /><i style={{ width: `${(m.parts / max) * 100}%` }} className="parts" /></div>
                  <b>{dollars(m.total)}</b>
                </div>
              ))}
              <div className="legend"><span><i className="labor" />Labor</span><span><i className="parts" />Parts</span></div>
            </div>
          ) : <p className="muted pad">No invoices in this period.</p>}
        </Panel>
        <Panel title="Payments received by method">
          <Table heads={['Method', 'Payments', 'Amount']} empty="No payments in this period."
            rows={(d.payments as Row[]).map(p => [p.method, p.n, dollars(p.total)])} />
        </Panel>
      </div>
      <div className="grid-2">
        <Panel title="Labor billed by tech">
          <Table heads={['Tech', 'Jobs', 'Labor billed']} empty="No assigned jobs invoiced."
            rows={(d.techs as Row[]).map(x => [x.name, x.jobs, dollars(x.labor)])} />
        </Panel>
        <Panel title="Most-used parts (by cost)">
          <Table heads={['Part', 'Qty', 'Cost']} empty="No parts used."
            rows={(d.topParts as Row[]).map(p => [<><b>{p.name}</b><small className="muted">{p.sku}</small></>, p.qty, dollars(p.cost)])} />
        </Panel>
      </div>
    </>
  );
}

/** Discounts given on invoices in the period: by person, by customer, each one. */
function DiscountReport({ from, to }: { from: string; to: string }) {
  const { data, error, reload } = useApi<Row>(`/api/reports/discounts?from=${from}&to=${to}`, [from, to]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return (<>
    <div className="stats"><Stat label="Discounts given" value={dollars(data.total)} hint={`${data.rows.length} discount${data.rows.length === 1 ? '' : 's'}`} /></div>
    <div className="grid-2">
      <Panel title="By person"><Table heads={['Given by', 'Discounts', 'Amount']} empty="No discounts in this period." rows={(data.byPerson as Row[]).map(x => [x.name, x.n, dollars(x.amount)])} /></Panel>
      <Panel title="By customer"><Table heads={['Customer', 'Discounts', 'Amount']} empty="No discounts in this period." rows={(data.byCustomer as Row[]).map(x => [x.name, x.n, dollars(x.amount)])} /></Panel>
    </div>
    <Panel title="Every discount">
      <Table heads={['Date', 'Invoice', 'Customer', 'Discount', 'Given by', 'Amount']} empty="No discounts in this period."
        rows={(data.rows as Row[]).map(r => [fmtDate(r.issued_on), r.invoice, r.customer, <>{r.description}{r.approved_by_name && <small className="muted"> · approved by {r.approved_by_name}</small>}</>, r.given_by || '—', dollars(r.amount)])}
        onRow={i => navigate('/work-orders/' + data.rows[i].wo_id)} />
    </Panel>
  </>);
}

const hrs = (min: number) => `${(min / 60).toFixed(1)} h`;
/** Parts runs: how much time goes to picking up parts, where, who, and what it cost in pay — is a parts runner worth it? */
function RunsReport({ from, to }: { from: string; to: string }) {
  const { data, error, reload } = useApi<Row>(`/api/reports/parts-runs?from=${from}&to=${to}`, [from, to]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const t = data.totals as Row;
  const maxV = Math.max(1, ...(data.byVendor as Row[]).map(v => v.minutes));
  return (<>
    <div className="stats">
      <Stat label="Runs" value={t.runs} />
      <Stat label="Time on the road" value={hrs(t.minutes)} hint={t.runs ? `average ${Math.round(t.minutes / t.runs)} min` : undefined} />
      <Stat label="Mechanic time lost (at pay rate)" value={dollars(t.pay)} tone={t.pay ? 'warn' : ''} />
      <Stat label="Charged to customers" value={dollars(t.charged)} />
    </div>
    <div className="grid-2">
      <Panel title="By vendor"><Table heads={['Vendor', 'Runs', 'Time', 'Average', '']} empty="No parts runs in this period."
        rows={(data.byVendor as Row[]).map(v => [v.name, v.runs, hrs(v.minutes), `${Math.round(v.minutes / Math.max(1, v.runs))} min`, <span className="run-bar" style={{ width: `${Math.round((v.minutes / maxV) * 160)}px` }} />])} /></Panel>
      <Panel title="Who went"><Table heads={['Person', 'Runs', 'Time', 'Pay cost']} empty="No parts runs in this period."
        rows={(data.byPerson as Row[]).map(p => [p.name, p.runs, hrs(p.minutes), dollars(p.pay)])} /></Panel>
    </div>
    <p className="form-note pad">Pay cost uses each person's hourly rate (salaried staff: their salary ÷ usual hours). A parts runner working those hours frees the same time for billable jobs.</p>
  </>);
}
